// frontend/src/hooks/useSubscription.ts
//
// Hook for managing user subscriptions via Stripe.
// Provides methods to upgrade, manage, and check subscription status.

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';

interface CheckoutOptions {
  priceId: string;
  successUrl?: string;
  cancelUrl?: string;
  paygCredits?: number;
}

interface SyncResult {
  synced: boolean;
  previousTier?: string;
  newTier?: string;
  message?: string;
}

interface CancelResult {
  success: boolean;
  immediate: boolean;
  cancelAt?: string;
  message: string;
}

interface UseSubscriptionReturn {
  loading: boolean;
  error: string | null;
  // Redirect to Stripe Checkout to start a subscription
  startCheckout: (options: CheckoutOptions) => Promise<void>;
  // Redirect to Stripe Customer Portal to manage subscription
  openCustomerPortal: (returnUrl?: string) => Promise<void>;
  // Sync subscription status from Stripe
  syncSubscription: () => Promise<SyncResult | null>;
  // Cancel subscription (at period end by default)
  cancelSubscription: (immediate?: boolean) => Promise<CancelResult | null>;
  // Clear any error state
  clearError: () => void;
}

export function useSubscription(): UseSubscriptionReturn {
  const { session, refreshProfile, ensureFreshToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = useCallback(async (options: CheckoutOptions) => {
    if (PAYMENTS_DISABLED) {
      setError(PAYMENTS_DISABLED_MESSAGE);
      return;
    }

    if (!session?.access_token) {
      setError('Please sign in to upgrade your subscription');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get a fresh token before making the request
      const token = await ensureFreshToken();
      if (!token) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            priceId: options.priceId,
            successUrl: options.successUrl || (options.paygCredits
              ? `${window.location.origin}/dashboard?credits_added=${options.paygCredits}`
              : `${window.location.origin}/dashboard?upgraded=true`),
            cancelUrl: options.cancelUrl || `${window.location.origin}/pricing`,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  }, [session, ensureFreshToken]);

  const openCustomerPortal = useCallback(async (returnUrl?: string) => {
    if (PAYMENTS_DISABLED) {
      setError(PAYMENTS_DISABLED_MESSAGE);
      return;
    }

    if (!session?.access_token) {
      setError('Please sign in to manage your subscription');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await ensureFreshToken();
      if (!token) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-portal`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            returnUrl: returnUrl || `${window.location.origin}/dashboard`,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to open customer portal');
      }

      if (data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Customer portal error:', err);
      setError(err instanceof Error ? err.message : 'Failed to open customer portal');
    } finally {
      setLoading(false);
    }
  }, [session, ensureFreshToken]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const syncSubscription = useCallback(async (): Promise<SyncResult | null> => {
    if (!session?.access_token) {
      setError('Please sign in to sync your subscription');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Get a fresh token before making the request
      const token = await ensureFreshToken();
      if (!token) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync subscription');
      }

      // Refresh profile to get updated tier
      await refreshProfile();

      return data as SyncResult;
    } catch (err) {
      console.error('Sync subscription error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync subscription');
      return null;
    } finally {
      setLoading(false);
    }
  }, [session, refreshProfile, ensureFreshToken]);

  const cancelSubscription = useCallback(async (immediate = false): Promise<CancelResult | null> => {
    if (!session?.access_token) {
      setError('Please sign in to cancel your subscription');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await ensureFreshToken();
      if (!token) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ immediate }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to cancel subscription');
      }

      // Refresh profile to get updated status
      await refreshProfile();

      return data as CancelResult;
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
      return null;
    } finally {
      setLoading(false);
    }
  }, [session, refreshProfile, ensureFreshToken]);

  return {
    loading,
    error,
    startCheckout,
    openCustomerPortal,
    syncSubscription,
    cancelSubscription,
    clearError,
  };
}
